import {CL_RX_METHOD, PRESCRIPTION_STATUS} from 'constants/success';
import {
    optionalNumberRule,
    validatePatientName,
} from 'desktop/pages/success/utils/validation/validation';
import {ItemsType, UPLOAD} from 'types/successPage';

/**
 * Gets the text for the cta button to open the popup
 */
export const getButtonText = (type: string | null) => {
    switch (type) {
        case PRESCRIPTION_STATUS.UPLOAD_SUCCESS:
            return 'Add My Prescription';
        case PRESCRIPTION_STATUS.VISTA:
        case PRESCRIPTION_STATUS.FILL_SUCCESS:
            return 'Confirm My Prescription';
        default:
            return null;
    }
};

/**
 * Gets the screen to go back to depending on the current rxMethod
 */
export const getPreviousMethod = (rxMethod: string) => {
    switch (rxMethod) {
        case CL_RX_METHOD.EYE_EXAM_ELIGIBILITY:
            return CL_RX_METHOD.EYE_EXAM;
        case CL_RX_METHOD.EYE_EXAM_ELIGIBILITY_FAILED:
            return CL_RX_METHOD.EYE_EXAM_ELIGIBILITY;
        case CL_RX_METHOD.MANUALLY_DOCTOR:
            return CL_RX_METHOD.DOCTOR;
        default:
            return '';
    }
};

/**
 * Returns if every item in the order has been updated
 */
export const getIsAllItemsOfOrderUpdated = (items: ItemsType) =>
    Boolean(items?.length) &&
    items?.every(item => {
        return (
            item?.prescriptionStatus &&
            item.prescriptionStatus !== PRESCRIPTION_STATUS.EYE_EXAM &&
            item.prescriptionStatus !== PRESCRIPTION_STATUS.VISIBLY
        );
    });

/**
 * Get whether they are on the verification step in TYP
 */
export const getIsVerification = ({
    hasDoctorDetails,
    isExamByIdExist = false,
    prescriptionMethod,
    status,
}: {
    status: string;
    prescriptionMethod?: number;
    hasDoctorDetails?: boolean;
    isExamByIdExist?: boolean;
}) =>
    (Boolean(status) &&
        status !== PRESCRIPTION_STATUS.EYE_EXAM &&
        status !== PRESCRIPTION_STATUS.VISIBLY) ||
    hasDoctorDetails ||
    prescriptionMethod === UPLOAD ||
    isExamByIdExist;

/**
 * checks if there is at least one item has eyeExam status
 */
export const getIsEyeExamVisible = (items: ItemsType) => {
    const isAtLeastOneItemHasEyeExamStatus = items?.some(item => {
        return (
            item?.prescriptionStatus === PRESCRIPTION_STATUS.EYE_EXAM ||
            item?.prescriptionStatus === PRESCRIPTION_STATUS.VISIBLY
        );
    });
    return !isAtLeastOneItemHasEyeExamStatus;
};

/** Function sending event to FullStory with OrderID
 * @param {string} name - Name of the event
 * @param {string} orderId - params for component
 */
export const sendFSEventWithOrderId = (name: string, orderId: string): void => {
    if (window.FS) {
        window.FS('trackEvent', {
            name,
            properties: {
                orderId,
            },
        });
    }
};

/**
 * Validates fields for doctor details
 */
export const validateField = (name: string, value: string) => {
    const isValidLength = Boolean(value.trim().length);
    switch (name) {
        case 'patientName':
            return !validatePatientName(value);
        case 'phone':
        case 'fax':
            return !optionalNumberRule(value) && isValidLength;
        default:
            return isValidLength;
    }
};

/**
 * gets the analytics label for the popup button
 */
export const getButtonLabel = (prescriptionStatus: string) => {
    switch (prescriptionStatus) {
        case PRESCRIPTION_STATUS.UPLOAD_SUCCESS:
        case PRESCRIPTION_STATUS.VISTA:
            return 'No Prescription Uploaded';
        case PRESCRIPTION_STATUS.FILL_SUCCESS:
            return 'Fill manually';
        default:
            return '';
    }
};

/**
 * Returns the correct text for the label in analytics events
 */
const getMethodText = (isCta: boolean) => ({
    [CL_RX_METHOD.DOCTOR]: isCta
        ? 'Fill manually - Add doctor details (Smart) - Confirm & Continue'
        : 'Fill manually - Add doctor details (Smart)',
    [CL_RX_METHOD.EYE_EXAM]: isCta ? 'Online Exam - Send Vision Test to My Email' : 'Online Exam',
    [CL_RX_METHOD.EYE_EXAM_ELIGIBILITY]: isCta
        ? 'Online Exam - Eligibility Test - Continue'
        : 'Online Exam - Eligibility Test',
    [CL_RX_METHOD.EYE_EXAM_ELIGIBILITY_FAILED]: isCta
        ? 'Online Exam - Eligibility test failure - Refill details'
        : 'Online Exam - Eligibility test failure',
    [CL_RX_METHOD.MANUALLY_DOCTOR]: isCta
        ? 'Fill manually - Add doctor details - Confirm & Continue'
        : 'Fill manually - Add doctor details',
    [CL_RX_METHOD.UPLOAD]: isCta
        ? 'Upload prescription - Confirm & Continue'
        : 'Upload prescription',
});

/**
 * get label for analytics event if its cta or not
 */
export const getLabel = (rxMethod: string, isCta = false, prescriptionStatus?: string) => {
    const label = getMethodText(isCta)[rxMethod];

    if (label && rxMethod === CL_RX_METHOD.UPLOAD) {
        const status = getButtonLabel(prescriptionStatus || '');
        return `${status ? `${status} - ` : ''}${label}`;
    }
    return label || `Fill manually - Add doctor details${isCta ? ' - Confirm & Continue' : ''}`;
};
